import { useEffect, useState } from 'react';
import { api, ifMatchFor, SUPPORTED_API_MINOR } from '../api/client';
import { qk, useConfig, useDevices, useHealth, useStateHistory } from '../api/queries';
import type { ConfigPatch } from '../api/types';
import { useAction } from '../components/feedback';
import { Async, Badge, Card, Field, KV, PageHeader } from '../components/ui';
import { byId, deviceName } from '../lib/domain';
import { MODE_LABEL, REASON_LABEL } from '../lib/labels';
import { dayTime } from '../lib/time';

export function SettingsPage() {
  return (
    <>
      <PageHeader title="Réglages & santé" subtitle="Configuration globale du service et état de ses composants." />
      <div className="split">
        <div className="split-side"><HealthCard /></div>
        <div className="split-main stack-l">
          <ConfigCard />
          <HistoryCard />
        </div>
      </div>
    </>
  );
}

function HealthCard() {
  const h = useHealth();
  const tone = (v: string) => (v === 'available' || v === 'running' || v === 'ok' ? 'ok' : v === 'disabled' ? 'neutral' : 'bad');
  return (
    <Card title="Santé du service">
      <Async q={{ data: h.data, isLoading: h.isLoading, error: h.error }}>
        {(d) => (
          <div className="stack">
            <KV items={[
              ['Statut', <Badge tone={tone(d.status)}>{d.status === 'ok' ? 'Opérationnel' : 'Dégradé'}</Badge>],
              ['Base de données', <Badge tone={tone(d.database)}>{d.database}</Badge>],
              ['Broker MQTT', <Badge tone={tone(d.mqtt)}>{d.mqtt}</Badge>],
              ['Moteur de règles', <Badge tone={tone(d.engine)}>{d.engine}</Badge>],
              ['Dernière collecte des sondes', dayTime(d.last_sensor_collection)],
              ['Dernière sauvegarde', dayTime(d.last_backup)],
              ['Version de l’API', <span className="mono">{d.api_version} (front : {SUPPORTED_API_MINOR}.x)</span>],
            ]} />
            {d.last_backup_error && <p className="alert alert-bad small">Sauvegarde : {d.last_backup_error}</p>}
            {d.last_control_error && <p className="alert alert-bad small">Pilotage : {d.last_control_error}</p>}
            <p className="small muted">Accès protégé par Cloudflare Access. Le jeton du service reste dans la Pages Function et n’est jamais envoyé au navigateur.</p>
          </div>
        )}
      </Async>
    </Card>
  );
}

function ConfigCard() {
  const c = useConfig();
  const { run, busy } = useAction();
  const [retry, setRetry] = useState('');
  const [timeout, setTimeoutS] = useState('');
  const [tol, setTol] = useState('');
  const [webhook, setWebhook] = useState('');
  useEffect(() => {
    if (!c.data) return;
    setRetry(String(c.data.heating_actuation_retry_count));
    setTimeoutS(String(c.data.heating_actuation_timeout_seconds));
    setTol(String(c.data.measurement_tolerance_c));
  }, [c.data]);

  const save = (body: ConfigPatch, msg: string) =>
    run(() => api.send('PATCH', '/config', { body, ifMatch: ifMatchFor('/config', c.data?.version) }), { success: msg, invalidate: [qk.config] });

  return (
    <Card title="Configuration">
      <Async q={{ data: c.data, isLoading: c.isLoading, error: c.error }}>
        {(cfg) => (
          <div className="stack">
            <form className="stack" onSubmit={(e) => {
              e.preventDefault();
              save({ heating_actuation_retry_count: +retry, heating_actuation_timeout_seconds: +timeout, measurement_tolerance_c: +tol }, 'Configuration enregistrée');
            }}>
              <div className="grid-3">
                <Field label="Tentatives par commande" htmlFor="c-retry" hint="1 à 10, première incluse"><input id="c-retry" type="number" min={1} max={10} value={retry} onChange={(e) => setRetry(e.target.value)} /></Field>
                <Field label="Délai par tentative (s)" htmlFor="c-to" hint="1 à 60"><input id="c-to" type="number" min={1} max={60} value={timeout} onChange={(e) => setTimeoutS(e.target.value)} /></Field>
                <Field label="Tolérance de mesure (°C)" htmlFor="c-tol" hint="Marge autour des cibles"><input id="c-tol" type="number" step="0.1" min={0} max={5} value={tol} onChange={(e) => setTol(e.target.value)} /></Field>
              </div>
              <div><button className="btn btn-primary" disabled={busy}>Enregistrer</button></div>
            </form>
            <div className="divider" />
            <form className="stack-s" onSubmit={(e) => { e.preventDefault(); save({ discord_webhook_url: webhook.trim() }, 'Webhook Discord remplacé'); setWebhook(''); }}>
              <Field label="Webhook Discord des alertes" htmlFor="c-discord" hint={cfg.discord_webhook_configured ? 'Configuré · jamais réaffiché. Saisir une nouvelle URL pour le remplacer.' : 'Non configuré : aucune alerte ne sera envoyée.'}>
                <input id="c-discord" type="url" className="mono" placeholder="https://discord.com/api/webhooks/…" value={webhook} onChange={(e) => setWebhook(e.target.value)} autoComplete="off" />
              </Field>
              <div className="row gap-s">
                <button className="btn" disabled={busy || !webhook.trim()}>Remplacer</button>
                {cfg.discord_webhook_configured && <button type="button" className="btn btn-danger" disabled={busy} onClick={() => save({ discord_webhook_url: null }, 'Webhook Discord effacé')}>Effacer</button>}
              </div>
            </form>
            <div className="divider" />
            <KV items={[
              ['Mode de repli', MODE_LABEL[cfg.default_mode]],
              ['Durée max. de mesure', `${cfg.measurement_max_duration_hours} h`],
              ['Cadence des sondes', `${cfg.measurement_sample_interval_minutes} min`],
              ['Stabilité', `${cfg.measurement_stability_minutes} min`],
              ['Plateau', `${cfg.measurement_plateau_minutes} min`],
              ['Sonde perdue après', `${cfg.measurement_sensor_timeout_minutes} min`],
            ]} />
          </div>
        )}
      </Async>
    </Card>
  );
}

function HistoryCard() {
  const [page, setPage] = useState(1);
  const q = useStateHistory(page, 20);
  const devices = useDevices();
  const map = byId(devices.data);
  const pages = q.data ? Math.max(1, Math.ceil(q.data.total / q.data.limit)) : 1;
  return (
    <Card title="Historique des modes" actions={<span className="small muted">page {page} / {pages}</span>}>
      <Async q={{ data: q.data?.data, isLoading: q.isLoading, error: q.error }} empty={<p className="small muted">Aucune transition.</p>}>
        {(rows) => (
          <div className="table-scroll">
            <table className="table">
              <thead><tr><th scope="col">Heure</th><th scope="col">Radiateur</th><th scope="col">Mode</th><th scope="col">Raison</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="mono nowrap">{dayTime(r.occurred_at)}</td>
                    <td>{deviceName(map.get(r.device_id ?? ''))}</td>
                    <td className={`strong text-${r.mode}`}>{MODE_LABEL[r.mode]}</td>
                    <td>{REASON_LABEL[r.reason]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Async>
      <div className="row gap-s">
        <button type="button" className="btn btn-s" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Précédent</button>
        <button type="button" className="btn btn-s" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Suivant</button>
      </div>
    </Card>
  );
}
